/**
 * An object that is disposable.
 */
export interface Disposable {
    /**
     * A function called on the object when it is disposed.
     */
    dispose(): void;
}

export type ActionOrDisposable = MsftSme.Action | Disposable;

export interface RegisterForDisposeFunction {
    (disposables: ActionOrDisposable[]): LifetimeManagerBase;
    (disposable: ActionOrDisposable): LifetimeManagerBase;
}

/**
 * An object that can limit the lifetime of other objects. When a LifetimeManager object
 * is disposed, it will dispose all other objects that were registered for disposal.
 */
export interface LifetimeManagerBase {
    /**
     * Registers an object to be disposed.  It will throw if the object doesn't have dispose method.
     *
     * @param disposable An object to be disposed once the LifetimeManager object itself is disposed.
     */
    registerForDispose: RegisterForDisposeFunction;
}

export interface LifetimeManager extends LifetimeManagerBase {
    /**
     * Create a createChildLifetime to localize the LifetimeManager.
     * It will provide the function on tracking who create it and when it dispose,
     * it will remove itself from Container's lifetimeManager
     */
    createChildLifetime(): DisposableLifetimeManager;
}

export interface DisposableLifetimeManager extends Disposable, LifetimeManager {
    /**
     * A value indicating whether or not the lifetime is disposed.
     */
    isDisposed(): boolean;
}

function checkDisposeFunc(disposable: any, executeDispose: boolean) {
    if (typeof disposable.dispose === 'function') {
        // NOTE : Don't null the lifetime manager or in race conditions registerForDispose() will throw a
        //        null exception. Internal logic in the triggerable lifetime manager handles being disposed
        //        and having disposables added to it.
        if (executeDispose) {
            disposable.dispose();
        }
    } else if (typeof disposable === 'function') {
        if (executeDispose) {
            disposable();
        }
    } else {
        return false;
    }

    return true;
}

/**
 * An object that tracks and invokes disposal callbacks. This can be used
 * in other classes that wish to implement LifetimeManager.
 */
export class TriggerableLifetimeManager implements DisposableLifetimeManager {
    private disposables: Disposable[] = [];
    private isDisposedInternal = false;
    private isDisposing = false;
    private container: TriggerableLifetimeManager = null;
    private children: Disposable[] = [];

    /**
     * Gets a value indicating whether or not the lifetime is disposed.
     */
    public isDisposed(): boolean {
        return this.isDisposedInternal;
    }

    /**
     * See interface.
     */
    public registerForDispose(disposable: any): TriggerableLifetimeManager {
        let disposables: Disposable[];
        if (Array.isArray(disposable)) {
            disposables = disposable;
        } else {
            disposables = [disposable];
        }

        disposables.forEach((item) => {
            this.registerForDisposeInternal(item);
        });

        return this;
    }

    /**
     * See interface.
     */
    public createChildLifetime(): DisposableLifetimeManager {
        const triggerableLifeTimeManager = new TriggerableLifetimeManager();
        triggerableLifeTimeManager.container = this;
        this.children.push(triggerableLifeTimeManager);
        return triggerableLifeTimeManager;
    }

    /**
     * Causes the instance to regard itself as disposed, and to trigger any
     * callbacks that were already registered.
     */
    public dispose(): void {
        if (!this.isDisposedInternal) {
            this.isDisposedInternal = true;
            this.isDisposing = true;

            // remove all children and _disposable
            [this.disposables, this.children].forEach((disposables) => {
                while (disposables.length) {
                    const disposable = disposables.pop();
                    try {
                        checkDisposeFunc(disposable, true);
                    } catch (err) {
                        // ignore errors when running the check dispose function
                    }
                }
            });

            // check if we need to remove self from container
            const container = this.container;
            if (container) {
                this.container = null; // break the back link
                container.unregisterChildForDispose(this);
            }

            this.isDisposing = false;
        }
    }

    private unregisterChildForDispose(disposable: Disposable) {
        const children = this.children;
        if (!this.isDisposing) {
            const index = children.lastIndexOf(disposable);
            if (index === (children.length - 1)) {
                children.pop();
            } else {
                // take it self out of the container.
                children.splice(index, 1);
            }
        }
    }

    private isRegistered(disposable: Disposable) {
        return (this.disposables.lastIndexOf(disposable) >= 0 || this.children.lastIndexOf(disposable) >= 0);
    }

    private registerForDisposeInternal(disposable: Disposable): void {
        let valid = !this.isDisposedInternal;

        if (!disposable) {
            valid = false;
        }

        if (valid) {
            this.disposables.push(disposable);
        }
    }
}

/**
 * Auto disposer class used with lifetime manager.
 */
export class Disposer implements Disposable {
    private callback: () => void;

    /**
     * Initializes a new instance of the Disposer class.
     *
     * @param callback the callback function.
     */
    constructor(callback: () => void) {
        this.callback = callback;
    }

    /**
     * dispose function called when lifetime is gone.
     */
    public dispose(): void {
        this.callback();
    }
}
